/**
 * BackgroundProcessingService.js
 * This service handles background processing of server events, ensuring that
 * connections and data are maintained regardless of which page the user is viewing.
 * 
 * It uses module-level state management, local storage, and the BroadcastChannel API
 * to maintain state across page refreshes and tabs.
 */

// Module-level state (persists across component renders)
let processingActive = false;
let lastHeartbeats = {};
let connectionStatus = {};
let broadcastChannel = null;

// Constants
const STORAGE_KEY = 'deepr_background_processing';
const HEARTBEAT_TIMEOUT = 180000; // 3 minutes in milliseconds
const HEALTH_CHECK_INTERVAL = 10000; // 10 seconds

/**
 * Initialize the background processing service
 */
const initialize = () => {
  // Only initialize once
  if (processingActive) return;
  
  // Set up broadcast channel for cross-tab communication
  try {
    broadcastChannel = new BroadcastChannel('deepr_background_channel');
    broadcastChannel.onmessage = handleBroadcastMessage;
  } catch (err) {
    console.error('BackgroundProcessingService: Failed to create BroadcastChannel:', err);
  }
  
  // Restore state from localStorage if available
  try {
    const savedState = localStorage.getItem(STORAGE_KEY);
    if (savedState) {
      const parsedState = JSON.parse(savedState);
      lastHeartbeats = parsedState.lastHeartbeats || {};
      connectionStatus = parsedState.connectionStatus || {};
      console.log('BackgroundProcessingService: Restored state from localStorage', { lastHeartbeats, connectionStatus });
    }
  } catch (err) {
    console.error('BackgroundProcessingService: Failed to restore state from localStorage:', err);
  }
  
  // Start listening for WebSocket events
  window.addEventListener('systemevent-update', handleSystemEvent);
  window.addEventListener('process-update', handleProcessEvent);
  window.addEventListener('log-update', handleLogEvent);
  
  // Set up handler for heartbeat events directly from WebSocketContext
  window.addEventListener('heartbeat-update', handleHeartbeatEvent);
  
  // Set up visibility change detection for browser tab focus/blur
  document.addEventListener('visibilitychange', handleVisibilityChange);
  
  // Set up health check timer
  setInterval(healthCheck, HEALTH_CHECK_INTERVAL);
  
  processingActive = true;
  console.log('BackgroundProcessingService: Initialized and running');
};

/**
 * Handle broadcast messages from other tabs
 */
const handleBroadcastMessage = (event) => {
  if (event.data.type === 'state_update') {
    // Update our local state with data from another tab
    if (event.data.lastHeartbeats) {
      lastHeartbeats = { ...lastHeartbeats, ...event.data.lastHeartbeats };
    }
    if (event.data.connectionStatus) {
      connectionStatus = { ...connectionStatus, ...event.data.connectionStatus };
    }
    console.log('BackgroundProcessingService: Updated state from another tab', { lastHeartbeats, connectionStatus });
  }
};

/**
 * Handle heartbeat events
 */
const handleHeartbeatEvent = (event) => {
  const { serverId, data, timestamp } = event.detail;
  if (!serverId) return;
  
  const serverIdLower = serverId.toLowerCase();
  lastHeartbeats[serverIdLower] = timestamp || Date.now();
  connectionStatus[serverIdLower] = 'connected';
  
  // Save state and broadcast to other tabs
  saveState();
  broadcastState();
};

/**
 * Handle system events
 */
const handleSystemEvent = (event) => {
  // System events also indicate the server is alive
  const { serverId } = event.detail;
  if (!serverId) return;
  
  const serverIdLower = serverId.toLowerCase();
  lastHeartbeats[serverIdLower] = Date.now();
  connectionStatus[serverIdLower] = 'connected';
  
  // Save state and broadcast to other tabs
  saveState();
  broadcastState();
};

/**
 * Handle process events
 */
const handleProcessEvent = (event) => {
  // Process events also indicate the server is alive
  const { serverId } = event.detail;
  if (!serverId) return;
  
  const serverIdLower = serverId.toLowerCase();
  lastHeartbeats[serverIdLower] = Date.now();
  connectionStatus[serverIdLower] = 'connected';
  
  // Save state and broadcast to other tabs
  saveState();
  broadcastState();
};

/**
 * Handle log events
 */
const handleLogEvent = (event) => {
  // Log events also indicate the server is alive
  const { serverId } = event.detail;
  if (!serverId) return;
  
  const serverIdLower = serverId.toLowerCase();
  lastHeartbeats[serverIdLower] = Date.now();
  connectionStatus[serverIdLower] = 'connected';
  
  // Save state and broadcast to other tabs
  saveState();
  broadcastState();
};

/**
 * Handle visibility change (tab focus/blur)
 */
const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    console.log('BackgroundProcessingService: Tab became visible, syncing state');
    // When tab becomes visible again, check if we need to update state
    healthCheck();
  }
};

/**
 * Health check to update connection status based on heartbeat timestamps
 */
const healthCheck = () => {
  const now = Date.now();
  let stateChanged = false;
  
  // Check all servers with last heartbeat
  Object.keys(lastHeartbeats).forEach(serverId => {
    const lastHeartbeat = lastHeartbeats[serverId];
    const timeSinceLastHeartbeat = now - lastHeartbeat;
    
    // Mark as disconnected if no heartbeat for HEARTBEAT_TIMEOUT
    if (timeSinceLastHeartbeat > HEARTBEAT_TIMEOUT) {
      if (connectionStatus[serverId] !== 'disconnected') {
        connectionStatus[serverId] = 'disconnected';
        stateChanged = true;
        console.log(`BackgroundProcessingService: Server ${serverId} marked as disconnected (no heartbeat for ${timeSinceLastHeartbeat}ms)`);
      }
    }
  });
  
  // If state changed, save and broadcast
  if (stateChanged) {
    saveState();
    broadcastState();
  }
};

/**
 * Save state to localStorage
 */
const saveState = () => {
  try {
    const state = {
      lastHeartbeats,
      connectionStatus,
      timestamp: Date.now()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (err) {
    console.error('BackgroundProcessingService: Failed to save state to localStorage:', err);
  }
};

/**
 * Broadcast state to other tabs
 */
const broadcastState = () => {
  if (!broadcastChannel) return;
  
  try {
    broadcastChannel.postMessage({
      type: 'state_update',
      lastHeartbeats,
      connectionStatus,
      timestamp: Date.now()
    });
  } catch (err) {
    console.error('BackgroundProcessingService: Failed to broadcast state:', err);
  }
};

/**
 * Get the current connection status for a server
 */
const getServerConnectionStatus = (serverId) => {
  if (!serverId) return 'unknown';
  return connectionStatus[serverId.toLowerCase()] || 'unknown';
};

/**
 * Get the last heartbeat timestamp for a server
 */
const getLastHeartbeatTimestamp = (serverId) => {
  if (!serverId) return null;
  return lastHeartbeats[serverId.toLowerCase()] || null;
};

/**
 * Get all server connection statuses
 */
const getAllServerConnectionStatuses = () => {
  return { ...connectionStatus };
};

/**
 * Clean up and shutdown background processing
 */
const cleanup = () => {
  if (!processingActive) return;
  
  window.removeEventListener('systemevent-update', handleSystemEvent);
  window.removeEventListener('process-update', handleProcessEvent);
  window.removeEventListener('log-update', handleLogEvent);
  window.removeEventListener('heartbeat-update', handleHeartbeatEvent);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  
  if (broadcastChannel) {
    broadcastChannel.close();
    broadcastChannel = null;
  }
  
  processingActive = false;
  console.log('BackgroundProcessingService: Cleaned up and shut down');
};

export default {
  initialize,
  cleanup,
  getServerConnectionStatus,
  getLastHeartbeatTimestamp,
  getAllServerConnectionStatuses
};
